import React from 'react';
import SEO from '../../components/SEO';
import SiteFooter from '../../components/SiteFooter';
import DirectAnswer from '../../components/DirectAnswer';
import { Link } from 'react-router-dom';
import { implantsActonPageSEO } from '../../data/seo';
import { 
  getMedicalConditionLD, 
  getMedicalProcedureLD, 
  getBreadcrumbLD,
  getFAQPageLD 
} from '../../data/structuredData';
import { implantsCondition, implantsProcedure } from '../../data/medicalSchemas';
import { implantsFAQs } from '../../data/faqs';
import { implantsBreadcrumbs } from '../../data/breadcrumbs';
import { Button } from '../../components/ui/button';
import MiniConsultButton from '../../components/MiniConsultButton';
import EnquireButton from '../../components/EnquireButton';
import { 
  Phone, 
  Smile,
  CheckCircle2,
  Award,
  Clock,
  MapPin,
  Instagram,
  Facebook,
  Twitter,
  Music2,
  Shield,
  Heart,
  Zap
} from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';

const ImplantsActon = () => {
  const phoneNumber = '07787814064';
  const whatsappNumber = '447787814064';
  const phoneLink = `tel:${phoneNumber}`;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const whatsappLink = isMobile
    ? `whatsapp://send?phone=${whatsappNumber}&text=Hi, I'd like to book a dental implants consultation`
    : `https://web.whatsapp.com/send?phone=${whatsappNumber}&text=Hi, I'd like to book a dental implants consultation`;

  return (
    <>
      {/* SEO Meta Tags & Structured Data */}
      <SEO
        title={implantsActonPageSEO.title}
        description={implantsActonPageSEO.description}
        path={implantsActonPageSEO.path}
        keywords={implantsActonPageSEO.keywords}
        image={implantsActonPageSEO.image}
        structuredData={[
          getMedicalConditionLD(implantsCondition),
          getMedicalProcedureLD(implantsProcedure),
          getBreadcrumbLD(implantsBreadcrumbs),
          getFAQPageLD(implantsFAQs),
        ]}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-sky-gray/20 shadow-sm">
        <div className="bg-gradient-to-r from-burnt-coral/5 to-sky-gray/10 border-b border-sky-gray/20">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <a href={phoneLink} className="flex items-center gap-2 text-sm md:text-base text-rabbit hover:text-burnt-coral transition-colors group">
                <Phone className="w-4 h-4 md:w-5 md:h-5 text-burnt-coral group-hover:scale-110 transition-transform" />
                <span className="font-semibold">07787 814 064</span>
              </a>
              <div className="flex items-center gap-3 md:gap-4">
                <a href="https://www.instagram.com/kartikpateldentist/" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61588647254315" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://twitter.com/restore_dental" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on X (Twitter)">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="https://www.tiktok.com/@restore.dental" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on TikTok">
                  <Music2 className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Smile className="w-8 h-8 text-burnt-coral" />
            <span className="text-xl md:text-2xl font-bold text-rabbit">restore.dental</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/">
              <Button variant="outline" size="sm" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white">
                Home
              </Button>
            </Link>
            <Button size="sm" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild>
              <a href={whatsappLink}>Book Now</a>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-gradient-to-br from-[#01A9B4]/10 via-white to-[#006A71]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-[#006A71]/10 rounded-full border-2 border-[#01A9B4]/20">
              <p className="text-sm font-medium text-[#006A71]">
                <MapPin className="w-4 h-4 inline mr-1" />
                Acton, West London W3
              </p>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-rabbit mb-6">
              Dental Implants in <span className="text-burnt-coral">Acton</span>
            </h1>
            <p className="text-xl md:text-2xl text-chocolate-malt mb-8">
              The permanent solution for missing teeth • Natural look & feel • Restore your confidence
            </p>
            <DirectAnswer
              question="How much do dental implants cost in Acton?"
              answer="A single dental implant with abutment and crown costs from £2,295 at Restore.Dental in Acton (3D CBCT planning included), with All-on-4 full-arch solutions from £12,000 per arch. Dr Kartik Patel BDS places implants using guided digital planning; treatment typically takes 3–6 months from placement to final crown, and implants can last 25+ years with good care."
              bullets={['From £2,295 incl. crown', 'CBCT-guided digital planning', 'Same-day temporary teeth possible', '0% finance from £191/month']}
              className="max-w-3xl mx-auto mb-8"
            />
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Button size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild>
                <a href={whatsappLink}>
                  <WhatsAppIcon className="w-5 h-5 mr-2" />
                  Free Consultation
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" asChild>
                <a href={phoneLink}>
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </a>
              </Button>
            <EnquireButton slug="dental-implants" about="Dental Implants" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" testId="hero-enquire-btn" />
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-chocolate-malt">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-burnt-coral" />
                <span>Expert Implant Placement</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-burnt-coral" />
                <span>0% Finance Available</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-burnt-coral" />
                <span>Lifetime Solution</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* What are Dental Implants */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                What are Dental Implants?
              </h2>
              <p className="text-lg text-chocolate-malt mb-4">
                Dental implants are the gold standard for replacing missing teeth. They consist of a titanium post that's surgically placed into your jawbone, acting as an artificial tooth root. A crown is then attached to create a natural-looking, fully functional replacement tooth.
              </p>
              <p className="text-lg text-chocolate-malt mb-4">
                Unlike dentures or bridges, implants are permanent and don't rely on adjacent teeth for support. They look, feel, and function just like your natural teeth.
              </p>
              
              <div className="bg-gradient-to-br from-[#01A9B4]/10 to-[#D4AF37]/5 rounded-xl p-6 border-l-4 border-[#01A9B4] mt-6">
                <p className="text-chocolate-malt">
                  <strong className="text-rabbit">Did you know?</strong> Dental implants have a 95-98% success rate and can last a lifetime with proper care. They're the closest you can get to restoring your natural teeth.
                </p>
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                Why Choose restore.dental for Dental Implants?
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <Award className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Expert Care</h3>
                  <p className="text-chocolate-malt">Dr Kartik Patel has extensive experience in dental implant placement and restoration, ensuring optimal results.</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <Zap className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Advanced Technology</h3>
                  <p className="text-chocolate-malt">We use 3D imaging and digital planning to ensure precise implant placement for predictable outcomes.</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <Heart className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Comprehensive Care</h3>
                  <p className="text-chocolate-malt">From initial consultation to final restoration, we handle every step of your implant journey in-house.</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <CheckCircle2 className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Flexible Finance</h3>
                  <p className="text-chocolate-malt">0% finance options available to make your treatment affordable with manageable monthly payments.</p>
                </div>
              </div>
            </div>

            {/* Benefits of Dental Implants */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                Benefits of Dental Implants
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-rabbit">Look & Feel Natural</strong>
                    <p className="text-chocolate-malt">Custom-designed to match your natural teeth in color, shape, and function</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-rabbit">Permanent Solution</strong>
                    <p className="text-chocolate-malt">With proper care, implants can last a lifetime — no need for replacement</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-rabbit">Preserve Jawbone</strong>
                    <p className="text-chocolate-malt">Stimulates bone growth and prevents the bone loss that occurs with missing teeth</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-rabbit">Eat Anything</strong>
                    <p className="text-chocolate-malt">No food restrictions — bite and chew with confidence just like natural teeth</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-rabbit">Protect Adjacent Teeth</strong>
                    <p className="text-chocolate-malt">Unlike bridges, implants don't require grinding down healthy neighboring teeth</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-rabbit">Boost Confidence</strong>
                    <p className="text-chocolate-malt">Smile, speak, and laugh without worry — no slipping or clicking like dentures</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* The Implant Process */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                The Implant Process at restore.dental
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#01A9B4] text-white rounded-full flex items-center justify-center font-bold text-xl">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-rabbit mb-2">Initial Consultation</h3>
                    <p className="text-chocolate-malt">Comprehensive examination, 3D scans, and treatment planning. We'll discuss your options and answer all your questions.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#01A9B4] text-white rounded-full flex items-center justify-center font-bold text-xl">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-rabbit mb-2">Implant Placement</h3>
                    <p className="text-chocolate-malt">The titanium implant is placed into your jawbone under local anesthetic. Most patients experience minimal discomfort.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#01A9B4] text-white rounded-full flex items-center justify-center font-bold text-xl">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-rabbit mb-2">Healing Period</h3>
                    <p className="text-chocolate-malt">Over 3-6 months, the implant fuses with your bone (osseointegration). We'll provide a temporary tooth during this time.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#01A9B4] text-white rounded-full flex items-center justify-center font-bold text-xl">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-rabbit mb-2">Crown Placement</h3>
                    <p className="text-chocolate-malt">Your custom-made crown is attached to the implant. The result: a beautiful, natural-looking tooth that's built to last.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Types of Implant Solutions */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                Implant Solutions We Offer
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-[#01A9B4]/10 to-white p-6 rounded-xl border-2 border-[#01A9B4]/20">
                  <h3 className="text-lg font-bold text-rabbit mb-2">Single Tooth</h3>
                  <p className="text-sm text-chocolate-malt">Replace one missing tooth with a single implant and crown</p>
                </div>
                <div className="bg-gradient-to-br from-[#01A9B4]/10 to-white p-6 rounded-xl border-2 border-[#01A9B4]/20">
                  <h3 className="text-lg font-bold text-rabbit mb-2">Multiple Teeth</h3>
                  <p className="text-sm text-chocolate-malt">Implant-supported bridges for several missing teeth</p>
                </div>
                <div className="bg-gradient-to-br from-[#01A9B4]/10 to-white p-6 rounded-xl border-2 border-[#01A9B4]/20">
                  <h3 className="text-lg font-bold text-rabbit mb-2">Full Arch</h3>
                  <p className="text-sm text-chocolate-malt">All-on-4/All-on-6 for complete upper or lower arch restoration</p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-burnt-coral/10 via-sky-gray/10 to-[#D4AF37]/10 rounded-2xl p-8 md:p-12 text-center border-2 border-burnt-coral">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">
                Ready to Restore Your Smile?
              </h2>
              <p className="text-lg text-chocolate-malt mb-6">
                Book your free consultation today. Let's discuss if dental implants are right for you.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild>
                  <a href={whatsappLink}>
                    <WhatsAppIcon className="w-5 h-5 mr-2" />
                    Book on WhatsApp
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" asChild>
                  <a href={phoneLink}>
                    <Phone className="w-5 h-5 mr-2" />
                    Call 07787 814 064
                  </a>
                </Button>
              <EnquireButton slug="dental-implants" about="Dental Implants" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" testId="cta-enquire-btn" />
              </div>
            </div>

            {/* Other Services */}
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-rabbit mb-6 text-center">
                Our Other Services in Acton
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/invisalign-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Invisalign</h3>
                  <p className="text-sm text-chocolate-malt">Clear aligners with remote monitoring</p>
                </Link>
                <Link to="/composite-bonding-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Composite Bonding</h3>
                  <p className="text-sm text-chocolate-malt">Transform your smile in one visit</p>
                </Link>
                <Link to="/cosmetic-dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Cosmetic Dentistry</h3>
                  <p className="text-sm text-chocolate-malt">Teeth whitening, veneers & more</p>
                </Link>
                <Link to="/dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">General Dentistry</h3>
                  <p className="text-sm text-chocolate-malt">Complete family dental care</p>
                </Link>
                <Link to="/iv-sedation-dental-implants" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all" data-testid="implants-sedation-link">
                  <h3 className="font-bold text-rabbit mb-2">Implants Under IV Sedation</h3>
                  <p className="text-sm text-chocolate-malt">Surgery you would rather not be aware of</p>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      <SiteFooter />

      {/* Mini Consult Button */}
      <MiniConsultButton />
    </>
  );
};

export default ImplantsActon;
